/**
 * Per-file offline buffer for cloud scenes.
 *
 * This is the persistence target for a cloud file's unsaved edits — kept
 * completely separate from the anonymous scratch canvas keys
 * (`STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS` / `_APP_STATE`). Opening `/f/<id>` must
 * never write the scratch keys (that would clobber unsaved scratch work), and
 * conversely the scratch canvas must never touch these per-file buffers.
 *
 * When a save fails (offline / server error) the current scene is buffered here
 * and flushed to the cloud once connectivity returns; on a successful save the
 * buffer is cleared.
 */

import { ExcalidrawElement } from "../../packages/excalidraw/element/types";
import { AppState } from "../../packages/excalidraw/types";

const KEY_PREFIX = "mindmap-file-buffer:";

const bufferKey = (fileId: string) => `${KEY_PREFIX}${fileId}`;

export interface BufferedCloudScene {
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState>;
  /** epoch ms of when the buffer was written (for diagnostics). */
  savedAt: number;
  /**
   * The server version this scene was built on. Replaying the buffer against
   * this (not whatever is current on reopen) is what makes the server refuse a
   * stale one and surface the conflict instead of silently overwriting.
   */
  baseVersion?: number | null;
}

/**
 * Persist the buffer. Returns `true` on success, `false` when the write failed
 * (e.g. `QuotaExceededError`, or storage disabled) so callers can react — a
 * silent no-op here means unsaved work that the user believes is buffered is
 * actually gone. The `console.error` is kept for diagnostics.
 */
export const saveCloudFileBuffer = (
  fileId: string,
  scene: {
    elements: readonly ExcalidrawElement[];
    appState: Partial<AppState>;
    baseVersion?: number | null;
  },
): boolean => {
  try {
    const payload: BufferedCloudScene = {
      elements: scene.elements,
      appState: scene.appState,
      savedAt: Date.now(),
      baseVersion: scene.baseVersion ?? null,
    };
    localStorage.setItem(bufferKey(fileId), JSON.stringify(payload));
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const loadCloudFileBuffer = (
  fileId: string,
): BufferedCloudScene | null => {
  try {
    const raw = localStorage.getItem(bufferKey(fileId));
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as BufferedCloudScene;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const clearCloudFileBuffer = (fileId: string): void => {
  try {
    localStorage.removeItem(bufferKey(fileId));
  } catch (error) {
    console.error(error);
  }
};
